import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useNavigate } from "react-router";
import { useAppSelector } from "../../../../app/hooks";
import { miwChartColors, tooltipMark, utahChartColors } from "../../shared/reportUtil";
import { AssignedTutorialUsageByWriter } from "./UsageFilteredByCourseViewModel";
import { studentUsageLink } from "./UsageReport";

interface ChartData {
	name: string;
	assignedLessonCount: number;
	assignedLessonCompletedCount: number;
	link: string;
}

function initializeOptions(data: ChartData[]) {
	const { config } = useAppSelector((state) => state.site);
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;

	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	}

	const option: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		title: {
			text: "Assigned Lessons by Student",
			top: 0,
			left: 0,
			padding: 0,
			textStyle: { fontSize: 16, fontWeight: 600, color: foregroundColor },
		},
		grid: {
			containLabel: true,
			top: 50,
			left: 20,
			bottom: 25,
		},
		xAxis: {
			type: "value",
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: "Number of Assigned Lessons",
			nameLocation: "middle",
			nameGap: 30,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		yAxis: {
			type: "category",
			data: data.map((d) => d.name).toSorted(),
			inverse: true,
			axisLabel: {
				padding: 25,
				lineHeight: 15,
				interval: 0,
				width: 180,
				overflow: "break",
				hideOverlap: false,
			},
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		tooltip: {
			trigger: "axis",
			formatter: (
				params: { axisValue: string; color: string; seriesName: string; value: string }[]
			) => {
				const label = `<div>${params[0].axisValue}</div>`;
				const lineItem = (p: any) => {
					return `<div>${tooltipMark(p.color)} ${p.seriesName}: <b>${p.value}</b></div>`;
				};
				const content = params.reduce((a: string, v: any) => a + lineItem(v), label);

				return content;
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animationDurationUpdate: 500,
		series: [
			{
				name: "Assigned Lessons",
				type: "bar",
				data: data.map((d) => ({ value: d.assignedLessonCount, link: d.link })),
				itemStyle: {
					color: chartColors[0],
				},
				barWidth: 24,
				cursor: "pointer",
				markPoint: { silent: true },
				label: {
					show: true,
					position: ["100%", "50%"],
					height: 12,
					width: 12,
					align: "center",
					verticalAlign: "middle",
					padding: 4,
					backgroundColor: "#fff",
					borderRadius: 12,
					borderColor: "inherit",
					borderWidth: 1,
					fontWeight: "bolder",
					fontSize: 10,
				},
			},
			{
				name: "Number Completed",
				type: "bar",
				data: data.map((d) => ({ value: d.assignedLessonCompletedCount, link: d.link })),
				itemStyle: {
					color: chartColors[1],
				},
				barWidth: 24,
				cursor: "pointer",
				label: {
					show: true,
					position: ["100%", "50%"],
					height: 12,
					width: 12,
					align: "center",
					verticalAlign: "middle",
					padding: 4,
					backgroundColor: "#fff",
					borderRadius: 12,
					borderColor: "inherit",
					borderWidth: 1,
					fontWeight: "bolder",
					fontSize: 10,
				},
			},
		],
	};
	return option;
}

interface Props {
	className?: string;
	courseId: number;
	data: AssignedTutorialUsageByWriter[];
}

export function AssignedLessonUsageChart(props: Props) {
	const navigate = useNavigate();

	const data: ChartData[] = props.data.map((d) => ({
		name: d.writerName,
		assignedLessonCount: d.assignedLessonCount,
		assignedLessonCompletedCount: d.assignedLessonCompletedCount,
		link: studentUsageLink(props.courseId, d.writerId),
	}));

	const options = initializeOptions(data);
	const chartHeight = props.data.length * 70 + 94;

	const onChartClick = (params: any) => {
		if (params.data.link) navigate(params.data.link);
	};

	if (!props.data || props.data.length == 0) return "";

	return (
		<ReactEcharts
			option={options}
			className={`w-full ${props.className}`}
			style={{
				height: chartHeight,
			}}
			onEvents={{
				click: onChartClick,
			}}
		/>
	);
}
